import i18nFactory from '@yandex-int/i18n';
import { uniqBy } from 'lodash';
import { replace } from 'redux-little-router';
import { Epic } from 'redux-observable';
import { EMPTY, Observable, of } from 'rxjs';

import { Action } from '../../actions';
import { changeLocation, showCommonDataFetchError } from '../../actions/common';
import {
    FETCH_RECOMMENDATIONS_GRID_SUMMARY,
    FETCH_RECOMMENDATIONS_ITEMS,
    FetchRecommendationsGridSummary,
    FetchRecommendationsItems,
    GET_RECOMMENDATIONS_GRID_ITEM_APPLY_STATUS,
    GET_RECOMMENDATIONS_GRID_SUMMARY_APPLY_STATUS,
    GetRecommendationsGridItemApplyStatus,
    getRecommendationsGridItemApplyStatus,
    GetRecommendationsGridSummaryApplyStatus,
    getRecommendationsGridSummaryApplyStatus,
    SUBMIT_RECOMMENDATIONS_GRID_DATA_ITEM,
    SUBMIT_RECOMMENDATIONS_GRID_SUMMARY,
    SubmitRecommendationsGridDataItem,
    SubmitRecommendationsGridSummary,
    updateRecommendationsGridDataItemStatus,
    updateRecommendationsGridSummary,
    updateRecommendationsGridSummaryStatus,
    updateRecommendationsItems
} from '../../actions/recommendations-grid';
import { FetchRecommendationsItemsScreenSummaryDataResult } from '../../api/recommendations/RecommendationsDataProvider';
import {
    isRecommendationSupported,
    mapRecommendationKeyFromApiFormat,
    mapRecommendationTypeFromApiFormat
} from '../../mappers/recommendation';
import { mapSummaryGridRecommendationFromApiFormat } from '../../mappers/recommendationsGrid';
import { confirm } from '../../service/Confirm/Confirm';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { showRequestResultNoticeMessage } from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import {
    getContextRecommendationApplyStatusMessage,
    getContextRecommendationErrorStatusMessage,
    getContextRecommendationWithRefreshErrorStatusMessage,
    getRecommendationsWithRefreshErrorStatusMessage,
    getSummaryRecommendationApplyStatusCount,
    getSummaryRecommendationApplyStatusMessage,
    getSummaryRecommendationConfirmMessage
} from '../../service/RecommendationsHelpers/RecommendationsHelpers';
import { RequestIndexResult } from '../../service/SegmentManager';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { GridRow } from '../../typings/domain';
import { GdRecommendationStatus, RecommendationsStatusesQuery, RecommendationStatus } from '../../typings/graphql-grid';
import { NoticeType, SortParams } from '../../typings/grid';
import { RecommendationType } from '../../typings/recommendations';
import { State } from '../../typings/state';

import * as keyset from './recommendations-grid.i18n';

const i18n = i18nFactory(keyset);

const fetchRecommendationsGridSummaryEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_RECOMMENDATIONS_GRID_SUMMARY).mergeMap(
        ({ login, recommendationType }: FetchRecommendationsGridSummary): Observable<Action> => {
            return Observable.fromPromise(
                deps.recommendationsDataProvider.fetchRecommendationsItemsScreenSummaryData(login, recommendationType)
            )
                .map((result: FetchRecommendationsItemsScreenSummaryDataResult) => {
                    const summaryData = result.data[0];

                    return updateRecommendationsGridSummary(
                        summaryData ? mapSummaryGridRecommendationFromApiFormat(summaryData) : undefined
                    );
                })
                .catch(() => Observable.of(showCommonDataFetchError(i18n('error-refresh'))));
        }
    );
};

export interface FetchRecommendationsGridEpicData {
    data: RequestIndexResult;
    login: string;
    type: RecommendationType;
    sortParams: SortParams;
}

const fetchRecommendationsGridDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(FETCH_RECOMMENDATIONS_ITEMS)
        .map(({ index, login, recommendationType, sortParams }: FetchRecommendationsItems) => {
            return {
                data: deps.segmentManager.requestIndex(index),
                type: recommendationType,
                login,
                sortParams
            };
        })
        .filter(data => data !== null)
        .filter(({ data: { isNeedFetch } }: FetchRecommendationsGridEpicData) => isNeedFetch)
        .mergeMap(({ login, type, sortParams, data: { offset, count } }: FetchRecommendationsGridEpicData) => {
            let recommendationsAction$;

            if (type === RecommendationType.switchOnAutotargeting) {
                recommendationsAction$ = Observable.fromPromise(
                    deps.recommendationsDataProvider.fetchCampaignsIds(login, type, sortParams)
                );
            } else {
                recommendationsAction$ = Observable.fromPromise(Promise.resolve([]));
            }

            return recommendationsAction$.mergeMap(
                (campaignsIds: Array<string>): Observable<Action> =>
                    Observable.fromPromise(
                        deps.recommendationsDataProvider.fetchRecommendationsItemsScreenGridData(
                            login,
                            type,
                            offset,
                            count,
                            campaignsIds,
                            sortParams
                        )
                    )
                        .map(result => {
                            const state = store$.value;
                            const enabledRecommendationsType = result.clientInfo.enabledRecommendationTypes
                                .filter(isRecommendationSupported)
                                .map(mapRecommendationTypeFromApiFormat);

                            if (enabledRecommendationsType.includes(type)) {
                                return updateRecommendationsItems(
                                    {
                                        ...result.data,
                                        data: result.data.data.reduce(
                                            (res: Array<GridRow>, item: GridRow, pos: number) => {
                                                res[offset + pos] = item;

                                                return res;
                                            },
                                            state.recommendationsGrid.gridData.data.slice()
                                        )
                                    },
                                    result.clientInfo,
                                    result.reqId
                                );
                            } else {
                                return showCommonDataFetchError(i18n('no-rights'));
                            }
                        })
                        .catch(() => {
                            return Observable.of(showCommonDataFetchError(i18n('error-refresh')));
                        })
            );
        });
};

const getRecommendationsGridSummaryApplyStatusEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(GET_RECOMMENDATIONS_GRID_SUMMARY_APPLY_STATUS).mergeMap(
        (action: GetRecommendationsGridSummaryApplyStatus): Observable<Action> => {
            const GET_STATUS_INTERVAL = 2000;

            const ulogin = store$.value.commonData.login;

            return Observable.timer(0, GET_STATUS_INTERVAL)
                .switchMap(() =>
                    Observable.fromPromise(
                        deps.recommendationsDataProvider.getRecommendationsStatus(ulogin, action.enabledForApplyKeys)
                    )
                )
                .first(
                    (data: RecommendationsStatusesQuery) =>
                        data.client.recommendationsStatuses.length === action.enabledForApplyKeys.length
                )
                .mergeMap(
                    (data: RecommendationsStatusesQuery): Observable<Action> => {
                        const { recommendationType, requestedKeys } = action;

                        const recommendationsStatusesFilteredByDone = data.client.recommendationsStatuses.filter(
                            item => item && item.status === RecommendationStatus.DONE
                        );

                        const { appliedItemsCount, requestedItemsCount } = getSummaryRecommendationApplyStatusCount(
                            recommendationType,
                            requestedKeys,
                            recommendationsStatusesFilteredByDone
                        );

                        const resultMessage = getSummaryRecommendationApplyStatusMessage(
                            appliedItemsCount,
                            requestedItemsCount,
                            recommendationType
                        );

                        showRequestResultNoticeMessage(requestedItemsCount, appliedItemsCount, resultMessage);

                        return of(
                            updateRecommendationsGridSummaryStatus(GdRecommendationStatus.DONE),
                            changeLocation(UrlHelpers.getRecommendationsBaseUrl(), { ulogin }, false)
                        );
                    }
                );
        }
    );
};

const getRecommendationsGridItemApplyStatusEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(GET_RECOMMENDATIONS_GRID_ITEM_APPLY_STATUS)
        .mergeMap((action: GetRecommendationsGridItemApplyStatus) => {
            const GET_STATUS_INTERVAL = 2000;

            const ulogin = store$.value.commonData.login;

            return Observable.timer(0, GET_STATUS_INTERVAL)
                .switchMap(() =>
                    Observable.fromPromise(
                        deps.recommendationsDataProvider.getRecommendationsStatus(ulogin, action.requestedKey)
                    )
                )
                .first(
                    (data: RecommendationsStatusesQuery) =>
                        data.client.recommendationsStatuses.length === action.requestedKey.length
                )
                .map((data: RecommendationsStatusesQuery) => {
                    const recommendationAppliedItem = data.client.recommendationsStatuses.filter(
                        item => item && item.status === RecommendationStatus.DONE
                    );

                    if (recommendationAppliedItem && recommendationAppliedItem.length > 0) {
                        showNoticeMessage(
                            NoticeType.success,
                            getContextRecommendationApplyStatusMessage(action.recommendationType, action.name)
                        );
                    } else {
                        showNoticeMessage(
                            NoticeType.error,
                            getContextRecommendationErrorStatusMessage(action.recommendationType, action.name)
                        );
                    }

                    return store$.value.recommendationsGrid.gridData.total === 1
                        ? changeLocation(UrlHelpers.getRecommendationsBaseUrl(), { ulogin }, false)
                        : replace(store$.value.router); // обновление грида происходит в обработчик CHANGED_LOCATION
                });
        });
};

const submitRecommendationsGridDataItemEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(SUBMIT_RECOMMENDATIONS_GRID_DATA_ITEM)
        .mergeMap(({ recommendationType, keys, id, name }: SubmitRecommendationsGridDataItem) => {
            const setProgressStatus$ = Observable.of(
                updateRecommendationsGridDataItemStatus(id, GdRecommendationStatus.IN_PROGRESS)
            );

            const makeRequest$ = Observable.fromPromise(
                deps.recommendationsMutationExecutor.executeRecommendation(keys)
            )
                .map(data => {
                    if (data.executeRecommendation.items.length === 0) {
                        throw Error();
                    }

                    return getRecommendationsGridItemApplyStatus(recommendationType, keys, name);
                })
                .catch(() => {
                    showNoticeMessage(
                        NoticeType.error,
                        getContextRecommendationWithRefreshErrorStatusMessage(recommendationType, name)
                    );

                    return Observable.of(updateRecommendationsGridDataItemStatus(id, GdRecommendationStatus.READY));
                });

            return Observable.concat(setProgressStatus$, makeRequest$);
        });
};

const submitRecommendationsGridSummaryEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(SUBMIT_RECOMMENDATIONS_GRID_SUMMARY)
        .mergeMap(({ recommendationType, keys }: SubmitRecommendationsGridSummary) => {
            let itemsLength: number;

            if (recommendationType === RecommendationType.removePagesFromBlackList) {
                itemsLength = uniqBy(keys, 'cid').length;
            } else {
                itemsLength = keys.length;
            }

            const confirmMessage = getSummaryRecommendationConfirmMessage(recommendationType, itemsLength);

            return Observable.fromPromise(confirm({ message: confirmMessage }))
                .mergeMap(
                    (): Observable<Action> => {
                        const setProgressStatus$ = Observable.of(
                            updateRecommendationsGridSummaryStatus(GdRecommendationStatus.IN_PROGRESS)
                        );

                        const makeRequest$ = Observable.fromPromise(
                            deps.recommendationsMutationExecutor.executeRecommendation(keys)
                        )
                            .map(data =>
                                getRecommendationsGridSummaryApplyStatus(
                                    recommendationType,
                                    keys,
                                    data.executeRecommendation.items.map(mapRecommendationKeyFromApiFormat)
                                )
                            )
                            .catch(() => {
                                showNoticeMessage(
                                    NoticeType.error,
                                    getRecommendationsWithRefreshErrorStatusMessage(recommendationType)
                                );

                                return Observable.of(
                                    updateRecommendationsGridSummaryStatus(GdRecommendationStatus.READY)
                                );
                            });

                        return Observable.concat(setProgressStatus$, makeRequest$);
                    }
                )
                .catch((): Observable<Action> => EMPTY);
        });
};

export {
    fetchRecommendationsGridSummaryEpic,
    fetchRecommendationsGridDataEpic,
    submitRecommendationsGridDataItemEpic,
    submitRecommendationsGridSummaryEpic,
    getRecommendationsGridSummaryApplyStatusEpic,
    getRecommendationsGridItemApplyStatusEpic
};
